import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useCloseOthersOnOpen } from "src/hooks/useCloseOthersOnOpen";
import usePopoverClose from "../../../../hooks/usePopoverClose";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip/index.tsx";
import { Container } from "./styles";

const Item = ({
  children, //
  disabled,
  tip,
  onClick,
  dropdownLabel,
  dropdown = false,
  closeDropdownOnClick = false,
  isMenuCollapsed,
  maxWidth,
  shouldDisableTooltip,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const chevronRef = useRef(null);

  const { containerRef: ref, handleBlur } = usePopoverClose(dropdownOpen, () => setDropdownOpen(false), {
    onEscapeFocus: () => chevronRef.current?.focus(),
  });

  const [isTruncated, setIsTruncated] = useState(false);
  const [fullText, setFullText] = useState("");

  useEffect(() => {
    const updateTruncation = () => {
      const container = ref?.current;
      if (!container) return;
      const textEl = container.querySelector(".item .open p");
      if (!textEl) return;
      const truncated = textEl.scrollWidth > textEl.clientWidth;
      setIsTruncated(truncated);
      setFullText(truncated ? textEl.textContent || "" : "");
    };

    updateTruncation();
    window.addEventListener("resize", updateTruncation);
    return () => window.removeEventListener("resize", updateTruncation);
  }, [ref]);

  // Focus first dropdown item when opened
  useEffect(() => {
    if (dropdownOpen && ref.current) {
      requestAnimationFrame(() => {
        const focusables = ref.current.querySelectorAll(
          'a[href], button, [tabindex="0"], input, select, textarea'
        );
        for (const el of focusables) {
          if (!el.classList.contains("open") && !el.classList.contains("chevron")) {
            el.focus();
            break;
          }
        }
      });
    }
  }, [dropdownOpen, ref]);

  const { announceOpen } = useCloseOthersOnOpen("sudowrite:toolbar-dropdown-open", () => setDropdownOpen(false), ["sudokit:select-open"]);

  const computedTip = tip || (isTruncated ? <>{fullText}</> : null);

  return (
    <SudoTooltip
      customContent={computedTip}
      disabled={dropdownOpen || !computedTip || shouldDisableTooltip}
      open={dropdownOpen ? false : undefined}
    >
      <Container
        className={classNames({ "toolbar-dropdown-open": dropdownOpen })}
        isMenuCollapsed={isMenuCollapsed}
        maxWidth={maxWidth}
        ref={ref}
        onBlur={handleBlur}
      >
        <div className={classNames("item", { disabled })}>
          <button
            type="button"
            className={classNames("open use-component-styles", { dropdown: !!dropdown })}
            disabled={disabled}
            onClick={(e) => {
              if (!onClick) return;
              if (closeDropdownOnClick) setDropdownOpen(false);
              onClick(e);
            }}
          >
            {children}
          </button>

          {dropdown && (
            <button
              type="button"
              ref={chevronRef}
              className="chevron use-component-styles"
              aria-label={dropdownLabel}
              aria-expanded={dropdownOpen}
              onClick={(e) => {
                e.stopPropagation();
                const willOpen = !dropdownOpen;
                setDropdownOpen(willOpen);
                if (willOpen) announceOpen();
              }}
            >
              <div className="arrow">
                <InlineSVG
                  aria-hidden="true"
                  src="/assets/down-chevron.svg"
                />
              </div>
            </button>
          )}

          {dropdownOpen &&
            (typeof dropdown === "function"
              ? dropdown({ close: () => setDropdownOpen(false) })
              : dropdown)}
        </div>
      </Container>
    </SudoTooltip>
  );
};

export default Item;
